import { useState } from "react";
import {
  convertImageForUpload,
  ImageConversionError,
  isConvertibleImage,
} from "../../../lib/convertImageForUpload";
import type { WorkOrderId } from "../../../lib/workIds";
import { useWorkOrderCommentsMutations } from "../../../queries/useWorkOrderComments";

const supportedContentTypes = ["image/jpeg", "image/png", "image/webp"];
export const maxAttachmentBytes = 25 * 1024 * 1024;
// A HEIC or TIFF original can be larger than its converted result, but an
// unbounded file would exhaust browser memory while decoding.
const maxConvertibleSourceBytes = 100 * 1024 * 1024;
export const attachmentAccept =
  "image/jpeg,image/png,image/webp,image/heic,image/heif,image/tiff,.jpg,.jpeg,.png,.webp,.heic,.heif,.tif,.tiff";
export const unsupportedAttachmentMessage =
  "Choose a JPEG, PNG, WebP, HEIC, HEIF, or TIFF image up to 25 MB.";

export function contentTypeForFile(file: File) {
  if (supportedContentTypes.includes(file.type)) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    default:
      return "application/octet-stream";
  }
}

export function isSupportedAttachment(file: File) {
  return (
    supportedContentTypes.includes(contentTypeForFile(file)) &&
    file.size > 0 &&
    file.size <= maxAttachmentBytes
  );
}

/** Whether a picked file can be uploaded, directly or after conversion. */
export function isSelectableAttachment(file: File) {
  if (isConvertibleImage(file))
    return file.size > 0 && file.size <= maxConvertibleSourceBytes;
  return isSupportedAttachment(file);
}

/**
 * Converts HEIC/HEIF and TIFF to JPEG/PNG so the upload only ever carries a
 * type the server accepts; other files pass through unchanged.
 */
export function prepareAttachmentFile(file: File) {
  return convertImageForUpload(
    file,
    maxAttachmentBytes,
    unsupportedAttachmentMessage,
  );
}

export async function checksumForFile(file: File) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)));
}

/** A direct Work Order upload that has not been attached yet. */
export type PendingWorkOrderAttachment = {
  file: File;
  uploadId: string | null;
  bytesUploaded: boolean;
};

export type WorkOrderAttachmentUpload = {
  directUpload: PendingWorkOrderAttachment | null;
  /** Name of the file being converted before upload, or null. */
  convertingFileName: string | null;
  isUploading: boolean;
  error: string | null;
  start: (file: File) => void;
  retry: () => void;
  /** Drops a failed pending upload so the user can choose another file. */
  discard: () => void;
  clearError: () => void;
};

/**
 * Uploads one image and attaches it to the Work Order. A failed attempt keeps
 * its upload session so a retry reuses the same `uploadId` and skips bytes the
 * server already has. Owned by the screen, not the attachments section, so a
 * pending retry survives the section unmounting (for example in Edit mode).
 */
export function useWorkOrderAttachmentUpload(
  workOrderId: WorkOrderId,
): WorkOrderAttachmentUpload {
  const mutations = useWorkOrderCommentsMutations(workOrderId);
  const [directUpload, setDirectUpload] =
    useState<PendingWorkOrderAttachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [convertingFileName, setConvertingFileName] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  async function convertThenAttach(file: File) {
    setError(null);
    setConvertingFileName(file.name);
    let prepared: File;
    try {
      prepared = await prepareAttachmentFile(file);
    } catch (cause) {
      setError(
        cause instanceof ImageConversionError
          ? cause.message
          : unsupportedAttachmentMessage,
      );
      return;
    } finally {
      setConvertingFileName(null);
    }
    setDirectUpload({ file: prepared, uploadId: null, bytesUploaded: false });
    void attach(prepared);
  }

  async function attach(
    file: File,
    existingUploadId: string | null = null,
    bytesUploaded = false,
  ) {
    setError(null);
    setIsUploading(true);
    try {
      const contentType = contentTypeForFile(file);
      let uploadId = existingUploadId;
      let uploadUrl: string | null = null;
      let hasUploadedBytes = bytesUploaded;
      const checksumSha256 = await checksumForFile(file);
      if (!uploadId) {
        uploadId = crypto.randomUUID();
        setDirectUpload({ file, uploadId, bytesUploaded: false });
        const session = await mutations.createUpload({
          uploadId,
          fileName: file.name,
          contentType,
          contentLength: file.size,
          checksumSha256,
        });
        uploadUrl = session.uploadUrl;
        hasUploadedBytes = session.isUploaded;
        setDirectUpload({ file, uploadId, bytesUploaded: hasUploadedBytes });
      }
      if (!hasUploadedBytes) {
        if (!uploadUrl) {
          const session = await mutations.createUpload({
            uploadId,
            fileName: file.name,
            contentType,
            contentLength: file.size,
            checksumSha256,
          });
          uploadUrl = session.uploadUrl;
          hasUploadedBytes = session.isUploaded;
        }
        if (!hasUploadedBytes) {
          if (!uploadUrl)
            throw new Error("The upload URL is unavailable; retry the upload.");
          const response = await fetch(uploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": contentType,
              "x-amz-checksum-sha256": checksumSha256,
            },
            body: file,
          });
          if (!response.ok)
            throw new Error(
              `Unable to upload ${file.name} (${response.status}).`,
            );
          setDirectUpload({ file, uploadId, bytesUploaded: true });
        }
      }
      await mutations.attachFile.mutateAsync(uploadId);
      setDirectUpload(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Unable to attach file.",
      );
    } finally {
      setIsUploading(false);
    }
  }

  return {
    directUpload,
    convertingFileName,
    isUploading,
    error,
    start(file) {
      // A failed upload must be retried or discarded first, so a new pick
      // never silently replaces it.
      if (isUploading || directUpload || convertingFileName) return;
      if (!isSelectableAttachment(file)) {
        setError(unsupportedAttachmentMessage);
        return;
      }
      if (isConvertibleImage(file)) {
        void convertThenAttach(file);
        return;
      }
      setDirectUpload({ file, uploadId: null, bytesUploaded: false });
      void attach(file);
    },
    retry() {
      if (!directUpload || isUploading) return;
      void attach(
        directUpload.file,
        directUpload.uploadId,
        directUpload.bytesUploaded,
      );
    },
    discard() {
      if (isUploading) return;
      setDirectUpload(null);
      setError(null);
    },
    clearError: () => setError(null),
  };
}
